/**
 * Reports the chat drawer's slide to the shell, from the wrap's own transition
 * events. It only listens and times: which events are the slide and which end
 * belongs to the newest one are decided in slideMotion.ts, and what a report
 * means for the stage is decided by useChatSlide, which owns the slide.
 */

import type { RefObject } from 'react';
import { useEffect } from 'react';

import { createSlideSequence, isSlideTransition, SLIDE_END_DEFER_MS } from './slideMotion';

export function useChatSlideMotion(
    wrapRef: RefObject<HTMLDivElement | null>, onMotion?: (isMoving: boolean) => void,
): void {
    useEffect((): (() => void) | undefined => {
        const el = wrapRef.current;
        if (!el) {
            return undefined;
        }
        const slides = createSlideSequence();
        let endTimer: ReturnType<typeof setTimeout> | undefined;

        const start = (event: TransitionEvent): void => {
            if (!isSlideTransition(event, el)) {
                return;
            }
            slides.begin();
            clearTimeout(endTimer);
            // This is the frame the slide really begins, which a heavy first paint
            // can push well past the commit that posed it. useChatSlide re-anchors
            // the stage window on the report, so panel bodies stay frozen for the
            // whole ease rather than for a window opened too early.
            onMotion?.(true);
        };
        const stop = (event: TransitionEvent): void => {
            if (!isSlideTransition(event, el)) {
                return;
            }
            if (event.type === 'transitioncancel') {
                onMotion?.(false);
                return;
            }
            const endedValue = slides.getCurrent();
            endTimer = setTimeout((): void => {
                if (slides.isCurrent(endedValue)) {
                    onMotion?.(false);
                }
            }, SLIDE_END_DEFER_MS);
        };

        el.addEventListener('transitionstart', start);
        el.addEventListener('transitionend', stop);
        el.addEventListener('transitioncancel', stop);
        // Unmounting mid-slide fires no transition event at all, so the live
        // posture has to be dropped here or it never lifts.
        return (): void => {
            el.removeEventListener('transitionstart', start);
            el.removeEventListener('transitionend', stop);
            el.removeEventListener('transitioncancel', stop);
            clearTimeout(endTimer);
            onMotion?.(false);
        };
    }, [wrapRef, onMotion]);
}
